import type { ICameraController, IViewPortController, IViewPortManipulationApi } from '@/engine/app/interfaces';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import { CallbackManager } from '@/engine/Utilities/CallbackManager';
import type * as THREE from 'three';

export class ViewPortManipulationApiBase implements IViewPortManipulationApi {
  private _viewPortController?: IViewPortController;

  private _centerPointProvider?: () => THREE.Vector3;

  private readonly _orientationChangedListeners: CallbackManager<(quaternion: THREE.Quaternion) => void> =
    new CallbackManager();

  // IViewPortManipulationApi
  public setViewPortController(viewPortController: IViewPortController | undefined): void {
    this._viewPortController = viewPortController;
    if (this._viewPortController) {
      this._viewPortController
        .getCameraController()
        .onCameraModified((cameraInfo) => this._orientationChangedListeners.invoke(cameraInfo.getCameraQuaternion()));
      this._orientationChangedListeners.invoke(this._viewPortController.getCameraController().getCameraQuaternion());
    }
  }

  public setCenterPointProvider(provider: () => THREE.Vector3): void {
    this._centerPointProvider = provider;
  }

  protected _getParentOrientation(): THREE.Quaternion {
    if (!this._viewPortController) {
      throw new Error('View Port Controller is not initialized yet');
    }
    return this._viewPortController.getCameraController().getCameraQuaternion();
  }

  protected _trackParentOrientation(callback: (quaternion: THREE.Quaternion) => void): CallbackSubscription {
    const ret = this._orientationChangedListeners.subscribe(callback);
    if (this._viewPortController) {
      callback(this._viewPortController.getCameraController().getCameraQuaternion());
    }
    return ret;
  }

  protected _getViewPortController(): IViewPortController {
    if (!this._viewPortController) {
      throw new Error('ViewPortController should be defined');
    }
    return this._viewPortController;
  }

  protected _getCameraController(): ICameraController {
    return this._getViewPortController().getCameraController();
  }

  protected _getCenterPoint(): THREE.Vector3 {
    return this._centerPointProvider?.() || this._getViewPortController().getCommonCenterPoint();
  }
}
